import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, ArrowRight, Mail, Phone } from "lucide-react";
import { PhoneOtpVerifier } from "@/components/PhoneOtpVerifier";

export default function Login() {
  const [mode, setMode] = useState("password"); // "password" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("+91");
  const [busy, setBusy] = useState(false);
  const { login, loginWithOtp } = useAuth();
  const nav = useNavigate();

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Signed in");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (idToken) => {
    setBusy(true);
    try {
      await loginWithOtp(idToken);
      toast.success("Signed in");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left visual */}
      <div className="hidden lg:flex relative bg-zinc-950 text-white flex-col justify-between p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url(https://static.prod-images.emergentagent.com/jobs/3d23799f-8a67-4a48-b563-d804085642a1/images/64a722f367680848059f4d72f57fa04f8275e25df508ce3fa8a55393f8a2b735.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-9 h-9 bg-white text-zinc-950 flex items-center justify-center">
            <Wrench className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">System</div>
            <div className="font-bold text-lg tracking-tight">PARTSGRID</div>
          </div>
        </div>

        <div className="relative z-10 space-y-6 max-w-md">
          <div className="label-eyebrow text-zinc-400">Inventory Control Platform</div>
          <h1 className="text-5xl font-bold leading-[1.05] tracking-tight">
            A control room for every spare part you handle.
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed">
            Manage manufacturers, models, part numbers, retail counter orders,
            invoices and payments — from one strict, scannable workspace.
          </p>
          <div className="grid grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
            {[
              ["05", "Roles"],
              ["∞", "Parts"],
              ["1", "Source of truth"],
            ].map(([n, l]) => (
              <div key={l} className="bg-zinc-950 p-5">
                <div className="font-mono text-2xl font-bold">{n}</div>
                <div className="label-eyebrow text-zinc-400 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 font-mono text-[11px] text-zinc-500 uppercase tracking-[0.15em]">
          © 2026 PartsGrid · Secure JWT Authentication
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="space-y-2">
            <div className="label-eyebrow">Sign in</div>
            <h2 className="text-3xl font-bold tracking-tight text-zinc-950">Welcome back.</h2>
            <p className="text-sm text-zinc-500">Choose how you want to sign in.</p>
          </div>

          {/* Mode tabs */}
          <div className="grid grid-cols-2 border border-zinc-300" data-testid="login-mode-tabs">
            <button
              type="button"
              onClick={() => setMode("password")}
              data-testid="login-mode-password"
              className={`flex items-center justify-center gap-2 py-2.5 text-[11px] uppercase tracking-wider font-semibold ${mode === "password" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
            >
              <Mail className="w-3.5 h-3.5" /> Email &amp; password
            </button>
            <button
              type="button"
              onClick={() => setMode("otp")}
              data-testid="login-mode-otp"
              className={`flex items-center justify-center gap-2 py-2.5 text-[11px] uppercase tracking-wider font-semibold ${mode === "otp" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
            >
              <Phone className="w-3.5 h-3.5" /> Mobile OTP
            </button>
          </div>

          {mode === "password" ? (
            <form onSubmit={submitPassword} className="space-y-4" data-testid="login-form-password">
              <div>
                <label className="label-eyebrow block mb-2">Email</label>
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border border-zinc-300 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent font-mono"
                  placeholder="you@company.com"
                  required
                  autoFocus
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label-eyebrow">Password</label>
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[11px] uppercase tracking-wider text-[#002FA7] hover:underline">
                    Forgot?
                  </Link>
                </div>
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-zinc-300 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent font-mono"
                  placeholder="••••••••"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="login-submit-button"
                className="group w-full bg-zinc-950 text-white text-sm uppercase tracking-wider font-semibold py-3 hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {busy ? "Signing in..." : "Sign in"}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="login-form-otp">
              <p className="text-xs text-zinc-500 font-mono">
                We'll send a 6-digit code to your registered phone. On verification, you'll be signed in.
              </p>
              <PhoneOtpVerifier
                phone={phone}
                onPhoneChange={setPhone}
                onVerified={submitOtp}
                disabled={busy}
                testidPrefix="login-otp"
                autoFocus
              />
              <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                Your phone must already be on file for a PartsGrid account.
              </div>
            </div>
          )}

          <div className="border-t border-zinc-200 pt-4 text-[11px] font-mono uppercase tracking-wider text-zinc-500 leading-relaxed">
            Access is by invitation only · Contact your administrator for credentials
          </div>
        </div>
      </div>
    </div>
  );
}
